import * as React from "react";
import { Link } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  ChevronDown,
  ChevronsUpDown,
  ChevronUp,
  Pencil,
  Search,
  Send,
} from "lucide-react";
import { useAuth } from "@/features/auth/auth-context";
import { canManageOrgUsers, isAdmin as checkIsAdmin } from "@/features/auth/role-utils";
import { apiFetch, APIError } from "@/lib/api-client";
import { toast } from "@/components/ui/toaster";
import { Field } from "@/components/ui/field";
import { Modal } from "@/components/ui/modal";
import { WarningCard } from "@/components/ui/warning-card";
import { formatRelative } from "@/lib/format-relative";
import { cn } from "@/lib/utils";

// Phase 14 — team-route. Liste aller user der org + invite-modal + role-
// change-modal + soft-delete. Schreib-aktionen admin-only (server enforced,
// UI versteckt knöpfe für non-admins).
//
// Sortierung: server liefert `role asc, display_name asc` als default; client
// kann via sort-controls overriden. Search: client-side, locale-aware,
// `display_name + email` substring-match (useDeferredValue debounced).

type Role = "admin" | "leitung" | "betreuer";
type SortKey = "role" | "display_name" | "last_login_at";
type SortDir = "asc" | "desc";

interface OrgUser {
  id: string;
  email: string;
  role: Role;
  display_name: string;
  has_password: boolean;
  last_login_at?: string;
  created_at: string;
}

interface ListUsersResponse {
  users: OrgUser[];
}

const inviteSchema = z.object({
  email: z
    .string()
    .min(3, "E-Mail erforderlich")
    .max(254, "E-Mail zu lang")
    .email("Ungültige E-Mail-Adresse"),
  display_name: z
    .string()
    .min(1, "Name erforderlich")
    .max(120, "Max 120 Zeichen"),
  role: z.enum(["admin", "leitung", "betreuer"]),
});

type InviteFormData = z.infer<typeof inviteSchema>;

const ROLE_LABEL: Record<Role, string> = {
  admin: "Admin",
  leitung: "Leitung",
  betreuer: "Betreuer:in",
};

const ROLE_BADGE: Record<Role, string> = {
  admin: "bg-amber-100 text-amber-900 ring-amber-300",
  leitung: "bg-sky-100 text-sky-900 ring-sky-300",
  betreuer: "bg-neutral-100 text-neutral-700 ring-neutral-300",
};

// Server-sort-order für default-state (deterministisch).
const ROLE_ORDER: Record<Role, number> = { admin: 0, leitung: 1, betreuer: 2 };

function loginStatusLabel(u: OrgUser): React.ReactNode {
  if (u.last_login_at) {
    return <>Aktiv (letzter Login {formatRelative(u.last_login_at)})</>;
  }
  return (
    <span className="text-[color:var(--color-warning)]">Noch nicht eingeloggt</span>
  );
}

function filterAndSort(
  users: OrgUser[],
  search: string,
  sortKey: SortKey,
  sortDir: SortDir,
): OrgUser[] {
  const q = search.trim().toLocaleLowerCase("de");
  const filtered = q
    ? users.filter(
        (u) =>
          u.display_name.toLocaleLowerCase("de").includes(q) ||
          u.email.toLocaleLowerCase("de").includes(q),
      )
    : users;
  const sign = sortDir === "asc" ? 1 : -1;
  return [...filtered].sort((a, b) => {
    if (sortKey === "role") {
      const diff = ROLE_ORDER[a.role] - ROLE_ORDER[b.role];
      if (diff !== 0) return diff * sign;
      return a.display_name.localeCompare(b.display_name, "de") * sign;
    }
    if (sortKey === "display_name") {
      return a.display_name.localeCompare(b.display_name, "de") * sign;
    }
    // last_login_at — undefined behandeln als "vor langer zeit" (epoch-0).
    const ta = a.last_login_at ? Date.parse(a.last_login_at) : 0;
    const tb = b.last_login_at ? Date.parse(b.last_login_at) : 0;
    return (ta - tb) * sign;
  });
}

function prefersReducedMotion(): boolean {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

export function TeamRoute(): React.ReactElement {
  const auth = useAuth();
  const qc = useQueryClient();
  // B9: einladen = admin | leitung. Rollen-änderung + deaktivieren = admin only.
  const canInvite = canManageOrgUsers(auth.user);
  const isAdmin = checkIsAdmin(auth.user);
  const isLeitung = auth.user?.role === "leitung";
  const currentUserId = auth.user?.userId;

  const [inviteOpen, setInviteOpen] = React.useState(false);
  const [roleModalUser, setRoleModalUser] = React.useState<OrgUser | null>(null);
  const [fadingOut, setFadingOut] = React.useState<Set<string>>(new Set());
  // Phase 14.1 — which row is currently (re)sending a magic-link (per-row
  // pending so only that button disables, not the whole list).
  const [sendingLinkId, setSendingLinkId] = React.useState<string | null>(null);

  const [search, setSearch] = React.useState("");
  const deferredSearch = React.useDeferredValue(search);
  const [sortKey, setSortKey] = React.useState<SortKey>("role");
  const [sortDir, setSortDir] = React.useState<SortDir>("asc");

  const usersQuery = useQuery({
    queryKey: ["org", "users"],
    queryFn: () => apiFetch<ListUsersResponse>("/v1/org/users"),
  });

  const visibleUsers = React.useMemo(() => {
    if (!usersQuery.data) return [];
    return filterAndSort(usersQuery.data.users, deferredSearch, sortKey, sortDir);
  }, [usersQuery.data, deferredSearch, sortKey, sortDir]);

  const createUser = useMutation({
    mutationFn: (data: InviteFormData) =>
      apiFetch<OrgUser>("/v1/org/users", { method: "POST", body: data }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["org", "users"] });
      toast("Benutzer angelegt — Anmelde-Link per E-Mail verschickt", "success");
      setInviteOpen(false);
    },
    onError: (err) => {
      if (err instanceof APIError && err.code === "user_exists") {
        toast("Diese E-Mail ist in deiner Organisation bereits vergeben", "warning");
      } else if (err instanceof APIError && err.code === "invalid_email") {
        toast("E-Mail-Format ungültig", "warning");
      } else if (err instanceof APIError && err.code === "admin_only") {
        toast("Nur Administratoren können Benutzer anlegen", "warning");
      } else if (err instanceof APIError && err.code === "rate_limited") {
        toast("Zu viele Einladungen — bitte später erneut versuchen", "warning");
      } else {
        toast("Anlegen fehlgeschlagen", "danger");
      }
    },
  });

  const updateRole = useMutation({
    mutationFn: ({ id, role }: { id: string; role: Role }) =>
      apiFetch<OrgUser>(`/v1/org/users/${id}`, {
        method: "PATCH",
        body: { role },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["org", "users"] });
      toast("Rolle geändert", "success");
      setRoleModalUser(null);
    },
    onError: (err) => {
      if (err instanceof APIError && err.code === "last_admin") {
        toast("Diese Organisation muss mindestens einen Admin behalten", "warning");
      } else if (err instanceof APIError && err.code === "cannot_modify_self") {
        toast("Deine eigene Rolle kannst du nicht ändern", "warning");
      } else if (err instanceof APIError && err.code === "admin_only") {
        toast("Nur Administratoren können Rollen ändern", "warning");
      } else {
        toast("Rollen-Änderung fehlgeschlagen", "danger");
      }
    },
  });

  // Note: api-client behandelt 204 jetzt nativ (return undefined), kein
  // catch-hack mehr nötig. Phase 14 — A1 fix bereits in rawFetch.
  const deactivate = useMutation({
    mutationFn: (id: string) =>
      apiFetch<void>(`/v1/org/users/${id}`, { method: "DELETE" }),
    onError: (err) => {
      if (err instanceof APIError && err.code === "last_admin") {
        toast("Diese Organisation muss mindestens einen Admin behalten", "warning");
      } else if (err instanceof APIError && err.code === "cannot_modify_self") {
        toast("Du kannst dich selbst nicht deaktivieren", "warning");
      } else {
        toast("Deaktivieren fehlgeschlagen", "danger");
      }
    },
  });

  // Phase 14.1 — (re)send a magic-link login mail to an existing user.
  // Server is enumeration-safe (always 200 on a known active target); the
  // success toast just confirms we triggered the send.
  const sendLink = useMutation({
    mutationFn: (id: string) =>
      apiFetch<{ status?: string }>(`/v1/org/users/${id}/magic-link`, {
        method: "POST",
      }),
    onMutate: (id) => setSendingLinkId(id),
    onSuccess: () => {
      toast("Anmelde-Link per E-Mail verschickt", "success");
    },
    onError: (err) => {
      if (err instanceof APIError && err.code === "rate_limited") {
        toast("Zu viele Anfragen — bitte kurz warten", "warning");
      } else if (err instanceof APIError && err.code === "forbidden") {
        toast("Nur Admin oder Leitung können Links senden", "warning");
      } else {
        toast("Senden fehlgeschlagen", "danger");
      }
    },
    onSettled: () => setSendingLinkId(null),
  });

  const handleDeactivate = React.useCallback(
    (u: OrgUser) => {
      if (
        !window.confirm(
          `${u.display_name} wirklich deaktivieren?\n\nDer Benutzer kann sich danach nicht mehr einloggen. Historische Einträge bleiben erhalten.`,
        )
      ) {
        return;
      }
      // Optimistic fade-out — sichtbares feedback bevor invalidate refetched.
      // Reduced-motion: fade-duration 0ms → instant.
      const fadeMs = prefersReducedMotion() ? 0 : 300;
      setFadingOut((s) => {
        const n = new Set(s);
        n.add(u.id);
        return n;
      });
      deactivate.mutate(u.id, {
        onSuccess: () => {
          toast("Benutzer deaktiviert", "success");
        },
        onSettled: () => {
          window.setTimeout(() => {
            setFadingOut((s) => {
              const n = new Set(s);
              n.delete(u.id);
              return n;
            });
            void qc.invalidateQueries({ queryKey: ["org", "users"] });
          }, fadeMs);
        },
      });
    },
    [deactivate, qc],
  );

  const handleSort = React.useCallback(
    (key: SortKey) => {
      if (sortKey === key) {
        setSortDir((d) => (d === "asc" ? "desc" : "asc"));
      } else {
        setSortKey(key);
        setSortDir(key === "last_login_at" ? "desc" : "asc");
      }
    },
    [sortKey],
  );

  return (
    <section className="space-y-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-wide text-neutral-500">
            <Link to="/einstellungen" className="hover:underline">
              Einstellungen
            </Link>{" "}
            / Team
          </p>
          <h1 className="text-3xl font-semibold">Team verwalten</h1>
          <p className="mt-1 text-sm text-neutral-600">
            Mitglieder deiner Einrichtung einladen, Rollen vergeben oder
            Zugriff entfernen.
          </p>
        </div>
        {canInvite && (
          <button
            type="button"
            onClick={() => setInviteOpen(true)}
            className="h-11 rounded-md bg-[color:var(--color-brand-primary-strong)] px-5 text-sm font-semibold text-white shadow-sm hover:opacity-90"
          >
            + Benutzer einladen
          </button>
        )}
      </header>

      {!isAdmin && !isLeitung && (
        <article className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          Nur Administratoren können Team-Mitglieder hinzufügen, deren Rolle
          ändern oder deaktivieren. Du siehst die Liste schreibgeschützt.
        </article>
      )}
      {isLeitung && (
        <article className="rounded-lg border border-sky-200 bg-sky-50 p-4 text-sm text-sky-900">
          Als Leitung kannst du Betreuer:innen einladen. Rollen-Änderungen und
          Deaktivierungen sind Administratoren vorbehalten.
        </article>
      )}

      {usersQuery.data && usersQuery.data.users.length > 0 && (
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="relative w-full sm:max-w-sm">
            <Search
              aria-hidden="true"
              strokeWidth={2}
              className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-[color:var(--color-text-secondary)]"
            />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Team durchsuchen"
              placeholder="Suchen …"
              className={cn(
                "h-11 w-full rounded-md border border-[color:var(--color-border)]",
                "bg-white pl-9 pr-3 text-sm",
                "placeholder:text-[color:var(--color-text-muted)]",
              )}
            />
          </div>
          {/* Mobile sort-dropdown (cards-mode); table-mode hat eigene
              th-buttons mit aria-sort, siehe TeamTable. */}
          <div className="flex items-center gap-2 md:hidden">
            <label
              htmlFor="team-sort"
              className="text-sm text-[color:var(--color-text-secondary)]"
            >
              Sortieren:
            </label>
            <select
              id="team-sort"
              value={`${sortKey}:${sortDir}`}
              onChange={(e) => {
                const parts = e.target.value.split(":");
                const k = parts[0];
                const d = parts[1];
                if (!k || !d) return;
                setSortKey(k as SortKey);
                setSortDir(d as SortDir);
              }}
              aria-label="Liste sortieren"
              className="h-11 rounded-md border border-[color:var(--color-border)] bg-white px-3 text-sm"
            >
              <option value="role:asc">Rolle (Admin zuerst)</option>
              <option value="display_name:asc">Name (A–Z)</option>
              <option value="display_name:desc">Name (Z–A)</option>
              <option value="last_login_at:desc">Letzter Login (neueste)</option>
            </select>
          </div>
        </div>
      )}

      <article className="overflow-hidden rounded-lg border border-[color:var(--color-border)] bg-white">
        {usersQuery.isPending && (
          <p className="p-6 text-sm text-neutral-500">Lade Team-Liste …</p>
        )}
        {usersQuery.isError && (
          <p className="p-6 text-sm text-[color:var(--color-danger)]">
            Liste konnte nicht geladen werden.
          </p>
        )}
        {usersQuery.data && usersQuery.data.users.length === 0 && (
          <p className="p-6 text-sm text-neutral-500">
            (unerwartet leer — bitte support kontaktieren)
          </p>
        )}
        {usersQuery.data && visibleUsers.length === 0 && usersQuery.data.users.length > 0 && (
          <p className="p-6 text-sm text-neutral-500">
            Keine Treffer für „{deferredSearch}".
          </p>
        )}
        {visibleUsers.length > 0 && (
          <>
            <div className="hidden md:block">
              <TeamTable
                users={visibleUsers}
                currentUserId={currentUserId}
                isAdmin={isAdmin}
                canSendLink={canInvite}
                sortKey={sortKey}
                sortDir={sortDir}
                fadingOut={fadingOut}
                deactivatePending={deactivate.isPending}
                sendingLinkId={sendingLinkId}
                onSort={handleSort}
                onOpenRoleModal={setRoleModalUser}
                onDeactivate={handleDeactivate}
                onSendLink={(u) => sendLink.mutate(u.id)}
              />
            </div>
            <ul className="space-y-3 p-3 md:hidden">
              {visibleUsers.map((u) => (
                <TeamCard
                  key={u.id}
                  user={u}
                  isSelf={u.id === currentUserId}
                  isAdmin={isAdmin}
                  canSendLink={canInvite}
                  fading={fadingOut.has(u.id)}
                  deactivatePending={deactivate.isPending}
                  sendingLink={sendingLinkId === u.id}
                  onOpenRoleModal={setRoleModalUser}
                  onDeactivate={handleDeactivate}
                  onSendLink={(u) => sendLink.mutate(u.id)}
                />
              ))}
            </ul>
          </>
        )}
      </article>

      <InviteModal
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        onSubmit={(data) => createUser.mutate(data)}
        isSubmitting={createUser.isPending}
        isLeitung={isLeitung}
      />

      <RoleModal
        user={roleModalUser}
        onClose={() => setRoleModalUser(null)}
        onSubmit={(role) => {
          if (!roleModalUser) return;
          updateRole.mutate({ id: roleModalUser.id, role });
        }}
        isSubmitting={updateRole.isPending}
      />
    </section>
  );
}

/* ────────────────────────────────────────────────────────────────────
 * Desktop: TeamTable (≥md)
 * ──────────────────────────────────────────────────────────────────── */

interface TeamTableProps {
  users: OrgUser[];
  currentUserId: string | undefined;
  isAdmin: boolean;
  canSendLink: boolean;
  sortKey: SortKey;
  sortDir: SortDir;
  fadingOut: Set<string>;
  deactivatePending: boolean;
  sendingLinkId: string | null;
  onSort: (key: SortKey) => void;
  onOpenRoleModal: (u: OrgUser) => void;
  onDeactivate: (u: OrgUser) => void;
  onSendLink: (u: OrgUser) => void;
}

function TeamTable({
  users,
  currentUserId,
  isAdmin,
  canSendLink,
  sortKey,
  sortDir,
  fadingOut,
  deactivatePending,
  sendingLinkId,
  onSort,
  onOpenRoleModal,
  onDeactivate,
  onSendLink,
}: TeamTableProps): React.ReactElement {
  return (
    <table className="w-full text-sm">
      <thead className="bg-[color:var(--color-surface-sunken)] text-xs uppercase tracking-wide text-neutral-500">
        <tr>
          <SortHeader
            label="Name"
            sortKeyValue="display_name"
            sortKey={sortKey}
            sortDir={sortDir}
            onSort={onSort}
          />
          <th className="px-4 py-3 text-left">E-Mail</th>
          <SortHeader
            label="Rolle"
            sortKeyValue="role"
            sortKey={sortKey}
            sortDir={sortDir}
            onSort={onSort}
          />
          <SortHeader
            label="Login-Status"
            sortKeyValue="last_login_at"
            sortKey={sortKey}
            sortDir={sortDir}
            onSort={onSort}
          />
          {canSendLink && <th className="px-4 py-3 text-right">Aktionen</th>}
        </tr>
      </thead>
      <tbody>
        {users.map((u) => {
          const isSelf = u.id === currentUserId;
          return (
            <tr
              key={u.id}
              className={cn(
                "border-t border-[color:var(--color-border)] last:border-b-0",
                "transition-opacity duration-300 ease-out",
                fadingOut.has(u.id) && "pointer-events-none opacity-0",
              )}
            >
              <td className="px-4 py-3 font-medium">
                {u.display_name}
                {isSelf && (
                  <span className="ml-2 text-xs text-neutral-500">(du)</span>
                )}
              </td>
              <td className="px-4 py-3 text-neutral-600">{u.email}</td>
              <td className="px-4 py-3">
                {isAdmin && !isSelf ? (
                  <button
                    type="button"
                    onClick={() => onOpenRoleModal(u)}
                    aria-label={`Rolle von ${u.display_name} ändern`}
                    className={cn(
                      "inline-flex h-9 items-center gap-2 rounded-md",
                      "border border-[color:var(--color-border)] bg-white px-3",
                      "text-sm font-medium text-[color:var(--color-text-primary)]",
                      "transition-colors duration-150 ease-out",
                      "hover:border-[color:var(--color-border-strong)] hover:bg-[color:var(--color-surface-sunken)]",
                    )}
                  >
                    <Pencil
                      aria-hidden="true"
                      strokeWidth={2}
                      className="size-3.5 text-[color:var(--color-text-secondary)]"
                    />
                    <span>{ROLE_LABEL[u.role]}</span>
                  </button>
                ) : (
                  <span
                    className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ring-1 ${ROLE_BADGE[u.role]}`}
                  >
                    {ROLE_LABEL[u.role]}
                  </span>
                )}
              </td>
              <td className="px-4 py-3 text-xs text-neutral-600">
                {loginStatusLabel(u)}
              </td>
              {canSendLink && (
                <td className="px-4 py-3 text-right">
                  <div className="flex items-center justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => onSendLink(u)}
                      disabled={sendingLinkId === u.id}
                      aria-label={`Anmelde-Link an ${u.display_name} per E-Mail senden`}
                      className={cn(
                        "inline-flex items-center gap-1.5 rounded-md",
                        "border border-[color:var(--color-border)] bg-white px-3 py-1.5",
                        "text-xs font-medium text-[color:var(--color-text-primary)]",
                        "transition-colors duration-150 ease-out",
                        "hover:border-[color:var(--color-border-strong)] hover:bg-[color:var(--color-surface-sunken)]",
                        "disabled:opacity-50",
                      )}
                    >
                      <Send
                        aria-hidden="true"
                        strokeWidth={2}
                        className="size-3.5 text-[color:var(--color-text-secondary)]"
                      />
                      <span>
                        {sendingLinkId === u.id ? "Sendet …" : "Anmelde-Link"}
                      </span>
                    </button>
                    {isAdmin && !isSelf && (
                      <button
                        type="button"
                        onClick={() => onDeactivate(u)}
                        disabled={deactivatePending}
                        aria-label={`${u.display_name} deaktivieren`}
                        className={cn(
                          "rounded-md border border-[color:var(--color-border)] px-3 py-1.5",
                          "text-xs font-medium text-[color:var(--color-danger)]",
                          "transition-colors duration-150 ease-out",
                          "hover:bg-[color:var(--color-surface-sunken)]",
                          "disabled:opacity-50",
                        )}
                      >
                        Deaktivieren
                      </button>
                    )}
                  </div>
                </td>
              )}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

interface SortHeaderProps {
  label: string;
  sortKeyValue: SortKey;
  sortKey: SortKey;
  sortDir: SortDir;
  onSort: (key: SortKey) => void;
}

function SortHeader({
  label,
  sortKeyValue,
  sortKey,
  sortDir,
  onSort,
}: SortHeaderProps): React.ReactElement {
  const isActive = sortKey === sortKeyValue;
  const ariaSort: "ascending" | "descending" | "none" = isActive
    ? sortDir === "asc"
      ? "ascending"
      : "descending"
    : "none";
  const nextDirLabel = isActive
    ? sortDir === "asc"
      ? "absteigend"
      : "aufsteigend"
    : "aufsteigend";
  return (
    <th className="px-4 py-3 text-left" aria-sort={ariaSort}>
      <button
        type="button"
        onClick={() => onSort(sortKeyValue)}
        aria-label={`Sortieren nach ${label} ${nextDirLabel}`}
        className={cn(
          "-mx-1 inline-flex items-center gap-1 rounded-sm px-1",
          "uppercase tracking-wide",
          "transition-colors duration-150 ease-out",
          isActive && "bg-[color:var(--color-brand-primary-soft)] text-[color:var(--color-text-primary)]",
        )}
      >
        {label}
        {isActive ? (
          sortDir === "asc" ? (
            <ChevronUp aria-hidden="true" className="size-3.5" strokeWidth={2} />
          ) : (
            <ChevronDown aria-hidden="true" className="size-3.5" strokeWidth={2} />
          )
        ) : (
          <ChevronsUpDown
            aria-hidden="true"
            className="size-3.5 opacity-40"
            strokeWidth={2}
          />
        )}
      </button>
    </th>
  );
}

/* ────────────────────────────────────────────────────────────────────
 * Mobile: TeamCard (<md)
 * ──────────────────────────────────────────────────────────────────── */

interface TeamCardProps {
  user: OrgUser;
  isSelf: boolean;
  isAdmin: boolean;
  canSendLink: boolean;
  fading: boolean;
  deactivatePending: boolean;
  sendingLink: boolean;
  onOpenRoleModal: (u: OrgUser) => void;
  onDeactivate: (u: OrgUser) => void;
  onSendLink: (u: OrgUser) => void;
}

function TeamCard({
  user,
  isSelf,
  isAdmin,
  canSendLink,
  fading,
  deactivatePending,
  sendingLink,
  onOpenRoleModal,
  onDeactivate,
  onSendLink,
}: TeamCardProps): React.ReactElement {
  return (
    <li
      className={cn(
        "rounded-lg border border-[color:var(--color-border)]",
        "bg-[color:var(--color-surface)] p-4 shadow-[var(--shadow-sm)]",
        "space-y-3",
        "transition-opacity duration-300 ease-out",
        fading && "pointer-events-none opacity-0",
      )}
    >
      <div>
        <h3 className="text-base font-semibold text-[color:var(--color-text-primary)]">
          {user.display_name}
          {isSelf && (
            <span className="ml-2 text-xs font-normal text-[color:var(--color-text-secondary)]">
              (du)
            </span>
          )}
        </h3>
      </div>
      <div className="space-y-1.5">
        <span
          className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ring-1 ${ROLE_BADGE[user.role]}`}
        >
          {ROLE_LABEL[user.role]}
        </span>
        <p className="text-sm text-[color:var(--color-text-secondary)]">
          {user.email}
        </p>
        <p className="text-xs text-[color:var(--color-text-secondary)]">
          {loginStatusLabel(user)}
        </p>
      </div>
      {(canSendLink || (isAdmin && !isSelf)) && (
        <div className="flex flex-wrap gap-2 border-t border-[color:var(--color-border)] pt-3">
          {canSendLink && (
            <button
              type="button"
              onClick={() => onSendLink(user)}
              disabled={sendingLink}
              aria-label={`Anmelde-Link an ${user.display_name} per E-Mail senden`}
              className={cn(
                "inline-flex h-11 items-center gap-2 rounded-md",
                "border border-[color:var(--color-border)] bg-white px-3",
                "text-sm font-medium text-[color:var(--color-text-primary)]",
                "transition-colors duration-150 ease-out",
                "hover:border-[color:var(--color-border-strong)] hover:bg-[color:var(--color-surface-sunken)]",
                "disabled:opacity-50",
              )}
            >
              <Send
                aria-hidden="true"
                strokeWidth={2}
                className="size-4 text-[color:var(--color-text-secondary)]"
              />
              <span>{sendingLink ? "Sendet …" : "Anmelde-Link senden"}</span>
            </button>
          )}
          {isAdmin && !isSelf && (
            <>
              <button
                type="button"
                onClick={() => onOpenRoleModal(user)}
                aria-label={`Rolle von ${user.display_name} ändern`}
                className={cn(
                  "inline-flex h-11 items-center gap-2 rounded-md",
                  "border border-[color:var(--color-border)] bg-white px-3",
                  "text-sm font-medium text-[color:var(--color-text-primary)]",
                  "transition-colors duration-150 ease-out",
                  "hover:border-[color:var(--color-border-strong)] hover:bg-[color:var(--color-surface-sunken)]",
                )}
              >
                <Pencil
                  aria-hidden="true"
                  strokeWidth={2}
                  className="size-4 text-[color:var(--color-text-secondary)]"
                />
                <span>Rolle: {ROLE_LABEL[user.role]}</span>
              </button>
              <button
                type="button"
                onClick={() => onDeactivate(user)}
                disabled={deactivatePending}
                aria-label={`${user.display_name} deaktivieren`}
                className={cn(
                  "inline-flex h-11 items-center rounded-md",
                  "border border-[color:var(--color-border)] bg-white px-3",
                  "text-sm font-medium text-[color:var(--color-danger)]",
                  "transition-colors duration-150 ease-out",
                  "hover:bg-[color:var(--color-surface-sunken)]",
                  "disabled:opacity-50",
                )}
              >
                Deaktivieren
              </button>
            </>
          )}
        </div>
      )}
    </li>
  );
}

/* ────────────────────────────────────────────────────────────────────
 * InviteModal (refactored: uses <Modal>)
 * ──────────────────────────────────────────────────────────────────── */

interface InviteModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: InviteFormData) => void;
  isSubmitting: boolean;
  /** B9 Eskalations-Schutz: leitung darf nur "betreuer" anlegen. */
  isLeitung?: boolean;
}

function InviteModal({
  open,
  onClose,
  onSubmit,
  isSubmitting,
  isLeitung = false,
}: InviteModalProps): React.ReactElement {
  const form = useForm<InviteFormData>({
    resolver: zodResolver(inviteSchema),
    defaultValues: { email: "", display_name: "", role: "betreuer" },
  });

  // Form-reset wenn modal von außen geschlossen wird (z.B. success).
  React.useEffect(() => {
    if (!open) {
      form.reset({ email: "", display_name: "", role: "betreuer" });
    }
  }, [open, form]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Benutzer einladen"
      description="Wir legen den Benutzer an und schicken ihm sofort einen Anmelde-Link per E-Mail (1 Stunde gültig, kein Passwort nötig). Falls der Link abläuft, kannst du ihn in der Liste über „Anmelde-Link senden“ erneut schicken."
      busy={isSubmitting}
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className={cn(
              "h-11 rounded-md border border-[color:var(--color-border)]",
              "px-4 text-sm font-medium",
              "transition-colors duration-150 ease-out",
              "hover:bg-[color:var(--color-surface-sunken)]",
              "disabled:cursor-not-allowed disabled:opacity-50",
            )}
          >
            Abbrechen
          </button>
          <button
            type="submit"
            form="invite-form"
            disabled={isSubmitting}
            className={cn(
              "h-11 rounded-md bg-[color:var(--color-brand-primary-strong)]",
              "px-4 text-sm font-semibold text-white",
              "transition-opacity duration-150 ease-out",
              "hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50",
            )}
          >
            {isSubmitting ? "Lege an …" : "Einladen"}
          </button>
        </>
      }
    >
      <form
        id="invite-form"
        onSubmit={form.handleSubmit(onSubmit)}
        className="space-y-3"
        noValidate
      >
        <Field label="E-Mail" error={form.formState.errors.email?.message}>
          <input
            type="email"
            inputMode="email"
            autoComplete="email"
            autoFocus
            placeholder="anna@beispiel-schule.de"
            disabled={isSubmitting}
            className="h-12 w-full rounded-md border border-[color:var(--color-border)] bg-white px-3 disabled:opacity-50"
            {...form.register("email")}
          />
        </Field>
        <Field
          label="Name"
          error={form.formState.errors.display_name?.message}
        >
          <input
            type="text"
            autoComplete="name"
            placeholder="Anna Beispiel"
            disabled={isSubmitting}
            className="h-12 w-full rounded-md border border-[color:var(--color-border)] bg-white px-3 disabled:opacity-50"
            {...form.register("display_name")}
          />
        </Field>
        <Field label="Rolle" error={form.formState.errors.role?.message}>
          {isLeitung ? (
            // B9 Eskalations-Schutz: leitung darf NUR "betreuer" anlegen.
            // Select-feld wird durch ein hidden input + lesbaren hinweis ersetzt.
            <>
              <input type="hidden" value="betreuer" {...form.register("role")} />
              <div className="flex h-12 items-center rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-surface-sunken)] px-3 text-sm text-[color:var(--color-text-secondary)]">
                Betreuer:in — Einträge erfassen
              </div>
              <p className="mt-1 text-xs text-[color:var(--color-text-muted)]">
                Leitung kann nur Betreuer:innen anlegen. Für andere Rollen bitte einen Admin kontaktieren.
              </p>
            </>
          ) : (
            <select
              disabled={isSubmitting}
              className="h-12 w-full rounded-md border border-[color:var(--color-border)] bg-white px-3 disabled:opacity-50"
              {...form.register("role")}
            >
              <option value="betreuer">Betreuer:in — Einträge erfassen</option>
              <option value="leitung">Leitung — Einträge erfassen + Räume</option>
              <option value="admin">Admin — Vollzugriff inkl. Team</option>
            </select>
          )}
        </Field>
      </form>
    </Modal>
  );
}

/* ────────────────────────────────────────────────────────────────────
 * RoleModal — confirm-flow für rollen-änderung
 * ──────────────────────────────────────────────────────────────────── */

interface RoleModalProps {
  user: OrgUser | null;
  onClose: () => void;
  onSubmit: (role: Role) => void;
  isSubmitting: boolean;
}

function RoleModal({
  user,
  onClose,
  onSubmit,
  isSubmitting,
}: RoleModalProps): React.ReactElement {
  const [newRole, setNewRole] = React.useState<Role>("betreuer");
  const currentRole = user?.role ?? "betreuer";

  // Sync form-state mit user-wechsel (auch bei close → user=null reset).
  React.useEffect(() => {
    setNewRole(user?.role ?? "betreuer");
  }, [user]);

  const unchanged = newRole === currentRole;
  const isAdminDemote = currentRole === "admin" && newRole !== "admin";

  return (
    <Modal
      open={user !== null}
      onClose={onClose}
      title="Rolle ändern"
      description={user ? `${user.display_name} · Aktuelle Rolle: ${ROLE_LABEL[currentRole]}` : undefined}
      busy={isSubmitting}
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className={cn(
              "h-11 rounded-md border border-[color:var(--color-border)]",
              "px-4 text-sm font-medium",
              "transition-colors duration-150 ease-out",
              "hover:bg-[color:var(--color-surface-sunken)]",
              "disabled:cursor-not-allowed disabled:opacity-50",
            )}
          >
            Abbrechen
          </button>
          <button
            type="submit"
            form="role-form"
            disabled={isSubmitting || unchanged}
            className={cn(
              "h-11 rounded-md bg-[color:var(--color-brand-primary-strong)]",
              "px-4 text-sm font-semibold text-white",
              "transition-opacity duration-150 ease-out",
              "hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50",
            )}
          >
            {isSubmitting ? "Speichere …" : "Speichern"}
          </button>
        </>
      }
    >
      <form
        id="role-form"
        onSubmit={(e) => {
          e.preventDefault();
          if (unchanged) return;
          onSubmit(newRole);
        }}
        className="space-y-4"
        noValidate
      >
        <div className="space-y-1.5">
          <span className="text-sm font-medium text-[color:var(--color-text-secondary)]">
            Aktuelle Rolle
          </span>
          <div>
            <span
              className={cn(
                "inline-flex items-center rounded-full px-3 py-1 text-sm font-medium",
                "bg-[color:var(--color-brand-primary-soft)] text-[color:var(--color-text-primary)]",
                "ring-1 ring-[color:var(--color-border)]",
              )}
            >
              {ROLE_LABEL[currentRole]}
            </span>
          </div>
        </div>
        <Field label="Neue Rolle">
          <select
            value={newRole}
            onChange={(e) => setNewRole(e.target.value as Role)}
            disabled={isSubmitting}
            aria-label="Neue Rolle wählen"
            className="h-12 w-full rounded-md border border-[color:var(--color-border)] bg-white px-3 disabled:opacity-50"
          >
            <option value="betreuer">Betreuer:in — Einträge erfassen</option>
            <option value="leitung">Leitung — Einträge erfassen + Räume</option>
            <option value="admin">Admin — Vollzugriff inkl. Team</option>
          </select>
        </Field>
        {isAdminDemote && (
          <WarningCard
            severity="warning"
            title="Vollzugriff wird entzogen"
            description="Diese Person verliert Zugriff auf Team-Verwaltung, Abrechnung und Organisations-Einstellungen."
          />
        )}
      </form>
    </Modal>
  );
}
